import yaml from 'js-yaml'
import { createBaseConfigFunction, DeployConfig, EnvironmentConfig, Json } from "../configuration/interface"
import { createEnvironmentRegExpString } from '../util/tagsHelpers'



export const defaultStages: string[] = [
  "ci-tools-build",
  "ci-tools-deploy"
]

export const defaultBuildTemplate: Json = {
  "image": "$CI_TOOLS_IMAGE",
  "stage": "ci-tools-build",
  "before_script": [
    "yarn install --frozen-lockfile --prefer-offline",
    "export CI_TOOLS_COMMIT_TIMESTAMP=\"$CI_COMMIT_TIMESTAMP\"",
    "export CI_TOOLS_COMMIT_AUTHOR=\"$CI_COMMIT_AUTHOR\"",
    "export CI_TOOLS_COMMIT_MESSAGE=\"$CI_COMMIT_MESSAGE\"",
    "yarn ci stage --stage=build --phase=before"
  ],
  "script": [
    "yarn ci stage --stage=build --phase=main"
  ],
  "after_script": [
    "yarn ci stage --stage=build --phase=after"
  ],
  "artifacts": {
    "paths": [
      "./build"
    ],
    "expire_in": "7 days"
  }
}

export const defaultDeployTemplate: Json = {
  "image": "$CI_TOOLS_IMAGE",
  "stage": "ci-tools-deploy",
  "before_script": [
    "yarn install --frozen-lockfile --prefer-offline",
    "export CI_TOOLS_COMMIT_TIMESTAMP=\"$CI_COMMIT_TIMESTAMP\"",
    "export CI_TOOLS_COMMIT_AUTHOR=\"$CI_COMMIT_AUTHOR\"",
    "export CI_TOOLS_COMMIT_MESSAGE=\"$CI_COMMIT_MESSAGE\"",
    "yarn ci stage --stage=deploy --phase=before"
  ],
  "script": [
    "yarn ci stage --stage=deploy --phase=main"
  ],
  "after_script": [
    "yarn ci stage --stage=deploy --phase=after"
  ]
}

export const defaultStopTemplate: Json = {
  "image": "$CI_TOOLS_IMAGE",
  "stage": "ci-tools-deploy",
  "before_script": [
    "yarn install --frozen-lockfile --prefer-offline",
    "export CI_TOOLS_COMMIT_TIMESTAMP=\"$CI_COMMIT_TIMESTAMP\"",
    "export CI_TOOLS_COMMIT_AUTHOR=\"$CI_COMMIT_AUTHOR\"",
    "export CI_TOOLS_COMMIT_MESSAGE=\"$CI_COMMIT_MESSAGE\"",
    "yarn ci stage --stage=stop --phase=before"
  ],
  "script": [
    "yarn ci stage --stage=stop --phase=main"
  ],
  "after_script": [
    "yarn ci stage --stage=stop --phase=after"
  ],
  "environment": {
    "action": "stop"
  },
  "dependencies": []
}


export const defaultRevertTemplate: Json = {
  "image": "$CI_TOOLS_IMAGE",
  "stage": "ci-tools-deploy",
  "before_script": [
    "yarn install --frozen-lockfile --prefer-offline",
    "export CI_TOOLS_COMMIT_TIMESTAMP=\"$CI_COMMIT_TIMESTAMP\"",
    "export CI_TOOLS_COMMIT_AUTHOR=\"$CI_COMMIT_AUTHOR\"",
    "export CI_TOOLS_COMMIT_MESSAGE=\"$CI_COMMIT_MESSAGE\"",
    "yarn ci stage --stage=revert --phase=before"
  ],
  "script": [
    "yarn ci stage --stage=revert --phase=main"
  ],
  "after_script": [
    "yarn ci stage --stage=revert --phase=after"
  ],
  "dependencies": []
}


export const createBaseConfig: createBaseConfigFunction = (
  buildTemplate,
  deployTemplate,
  stopTemplate,
  stages,
  revertTemplate,
) => {
  const baseConfig = {
    stages,
    ".build": buildTemplate,
    ".deploy": deployTemplate,
    ".stop": stopTemplate,
    ".revert": revertTemplate
  }
  return baseConfig
}

const createEnvironmentBuildCiConfig = (environmentConfig: EnvironmentConfig) => {

  const { environmentTagIsSemver, environmentTagIsVersioned, environmentName, isMultiEnv } = environmentConfig
  // RegExp part for any string for multiEnv instead of branch which is unknownNow
  const branch = isMultiEnv ? '.*?' : undefined
  const environmentTagRegExpString = createEnvironmentRegExpString(environmentTagIsSemver, environmentTagIsVersioned, environmentName, branch, false)
  const environmentBuildCiConfig = {
    "extends": ".build",
    "rules": [
      {
        "if": `$CI_COMMIT_TAG  =~ /${environmentTagRegExpString}/`
      }
    ]
  }
  return environmentBuildCiConfig
}

const createEnvironmentDeployCiConfig = (environmentConfig: EnvironmentConfig) => {
  const { environmentName, auto_stop_in, deployManual, environmentTagIsSemver, environmentTagIsVersioned, isMultiEnv } = environmentConfig

  // RegExp part for any string for multiEnv instead of branch which is unknownNow
  const branch = isMultiEnv ? '.*?' : undefined
  const environmentTagRegExpString = createEnvironmentRegExpString(environmentTagIsSemver, environmentTagIsVersioned, environmentName, branch, false)
  // TODO better type
  const environmentDeployCiConfig: any = {
    "extends": ".deploy",
    "rules": [
      {
        "if": `$CI_COMMIT_TAG  =~ /${environmentTagRegExpString}/`
      }
    ],
    "environment": {
      "name": `${environmentName}/$CI_COMMIT_TAG`,
    }
  }
  if (auto_stop_in) {
    environmentDeployCiConfig.environment.on_stop = `stop_${environmentName}`
    environmentDeployCiConfig.environment.auto_stop_in = auto_stop_in
  }
  if (deployManual) {
    environmentDeployCiConfig.rules[0].when = "manual"
  }
  return environmentDeployCiConfig
}

const createEnvironmentStopCiConfig = (environmentConfig: EnvironmentConfig) => {
  const { environmentTagIsSemver, environmentTagIsVersioned, environmentName, isMultiEnv } = environmentConfig
  // RegExp part for any string for multiEnv instead of branch which is unknownNow
  const branch = isMultiEnv ? '.*?' : undefined
  const environmentTagRegExpString = createEnvironmentRegExpString(environmentTagIsSemver, environmentTagIsVersioned, environmentName, branch, false)
  const environmentStopCiConfig = {
    "extends": ".stop",
    "rules": [
      {
        "if": `$CI_COMMIT_TAG  =~ /${environmentTagRegExpString}/`,
        "when": "manual"
      }
    ],
    "environment": {
      "name": `${environmentName}/$CI_COMMIT_TAG`
    }
  }
  return environmentStopCiConfig
}

// Manual job switching a revertible environment back to the previous build
const createEnvironmentRevertCiConfig = (environmentConfig: EnvironmentConfig) => {
  const { environmentTagIsSemver, environmentTagIsVersioned, environmentName, isMultiEnv } = environmentConfig
  // RegExp part for any string for multiEnv instead of branch which is unknownNow
  const branch = isMultiEnv ? '.*?' : undefined
  const environmentTagRegExpString = createEnvironmentRegExpString(environmentTagIsSemver, environmentTagIsVersioned, environmentName, branch, false)
  const environmentRevertCiConfig = {
    "extends": ".revert",
    "rules": [
      {
        "if": `$CI_COMMIT_TAG  =~ /${environmentTagRegExpString}/`,
        "when": "manual"
      }
    ]
  }
  return environmentRevertCiConfig
}

const createEnvironmentCiConfig = (environmentConfig: EnvironmentConfig) => {
  // TODO better type
  const environmentCiConfig: any = {}
  const { auto_stop_in, revertible, environmentName } = environmentConfig
  environmentCiConfig[`build_${environmentName}`] = createEnvironmentBuildCiConfig(environmentConfig)
  environmentCiConfig[`deploy_${environmentName}`] = createEnvironmentDeployCiConfig(environmentConfig)
  if (auto_stop_in) {
    environmentCiConfig[`stop_${environmentName}`] = createEnvironmentStopCiConfig(environmentConfig)
  }
  if (revertible) {
    environmentCiConfig[`revert_${environmentName}`] = createEnvironmentRevertCiConfig(environmentConfig)
  }
  return environmentCiConfig
}

export const createCiConfig = (deployConfig: DeployConfig) => {
  const { environments } = deployConfig
  const baseConfig = (deployConfig.createBaseConfig || createBaseConfig)(defaultBuildTemplate, defaultDeployTemplate, defaultStopTemplate, defaultStages, defaultRevertTemplate)
  // TODO better type
  const ciConfig = environments.reduce((ciConfig: any, environmentConfig) => {
    ciConfig = { ...ciConfig, ...createEnvironmentCiConfig(environmentConfig) }
    return ciConfig
  }, baseConfig)

  const yamlCiConfig = yaml.dump(ciConfig, {
    lineWidth: -1,

  })

  return yamlCiConfig
}